import { Button, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import moment from 'moment';

import {
  CustomsQueryAdditionalDocument,
  DeclarantCustomsQueryAdditionalDocumentCode,
  DeclarantCustomsQueryErrors,
  EORIGIN_DATE_FORMAT_WITH_TIME,
} from '@e-origin/shared';

import { TextInput } from '@components';
import { useDeclarant, useGenerateCustomsQueryTestFile, useSendGuaranteeQuery } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';

const additionalDocumentCategoryCodes = {
  [DeclarantCustomsQueryAdditionalDocumentCode.Z01]: 'Guarantee provided',
  [DeclarantCustomsQueryAdditionalDocumentCode.Z02]: 'Balance (Positive)',
  [DeclarantCustomsQueryAdditionalDocumentCode.Z03]: 'Balance (Negative)',
};

const additionalDocumentTypeNames = {
  AZH: 'AZH - Activity-Based Guarantee',
};

export const GuaranteeQuery = () => {
  const { data: declarant, isPending } = useDeclarant();
  const { data: settings } = useSettings();
  const sendGuaranteeQuery = useSendGuaranteeQuery();
  const generateTestFile = useGenerateCustomsQueryTestFile();

  const handleSubmit = async () => {
    await sendGuaranteeQuery.mutateAsync();
  };

  if (isPending) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Stack gap="xl">
      <Stack align="start">
        <TextInput value={declarant.identificationNumber ?? ''} label="Declarant identification number" data-pw="declarant-identification-number" disabled />
        <Button onClick={handleSubmit} loading={sendGuaranteeQuery.isPending}>
          Send request
        </Button>
        {!settings?.isProd && (
          <Button color="gray" onClick={() => generateTestFile.mutate()} loading={generateTestFile.isPending}>
            Generate test file
          </Button>
        )}
      </Stack>
      {declarant?.customsQuery?.requestForGuarantee?.response && (
        <>
          <Group>
            <Title size="md">Date:</Title>
            <Text>{moment(declarant.customsQuery.requestForGuarantee.response.issueDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
          </Group>
          {declarant.customsQuery.requestForGuarantee.response.additionalDocuments.map((document: CustomsQueryAdditionalDocument, index: number) => (
            <Stack mb="md" key={index}>
              <Title size="md">{additionalDocumentCategoryCodes[document.categoryCode]}</Title>
              <Text>Type: {additionalDocumentTypeNames[document.typeName]}</Text>
              <Text>Amount: {document.amount}</Text>
            </Stack>
          ))}
          {declarant.customsQuery.requestForGuarantee.response.error?.validationCode && (
            <Group>
              <Text c="red" fw="bold">
                Error:
              </Text>
              <Text>{declarant.customsQuery.requestForGuarantee.response.error.validationCode}</Text>
              <Text>{DeclarantCustomsQueryErrors[declarant.customsQuery.requestForGuarantee.response.error.validationCode]}</Text>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};
